/**
 * Payout History View
 * Shows list of payout runs with filtering
 */

import React, { useState, useEffect, useRef, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  CircularProgress,
  Alert,
  Chip,
  Pagination,
  Autocomplete,
  TextField,
} from "@mui/material";
import { ArrowBack, Visibility } from "@mui/icons-material";
import { getPayoutRuns } from "../services/payoutService.js";
import { getPayeeOptions } from "../../finance/services/payeeOptionsService.js";
import { logError, logInfo } from "@/shared/utils/logger.js";
import {
  PageShell,
} from "@/shared/components/pageShell";

const formatCurrency = (amount) => {
  if (amount === null || amount === undefined) return "R0.00";
  return `R${Number(amount).toLocaleString("en-ZA", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

const formatDate = (dateString) => {
  if (!dateString) return "N/A";
  try {
    return new Date(dateString).toLocaleDateString("en-ZA", {
      dateStyle: "medium",
    });
  } catch {
    return dateString;
  }
};

export default function PayoutHistoryView() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [runs, setRuns] = useState([]);
  const [pagination, setPagination] = useState({ total: 0, limit: 20, offset: 0 });
  const [error, setError] = useState(null);
  const [payeeOptions, setPayeeOptions] = useState([]);
  const [loadingOptions, setLoadingOptions] = useState(false);
  const [selectedPayee, setSelectedPayee] = useState(null);
  const debounceTimerRef = useRef(null);
  const inputValueRef = useRef("");

  // Load payee options on mount
  useEffect(() => {
    const loadPayeeOptions = async () => {
      try {
        setLoadingOptions(true);
        const options = await getPayeeOptions();
        setPayeeOptions(options);
        
        // Set selected payee from URL if exists
        const payeeIdFromUrl = searchParams.get("payeeId");
        if (payeeIdFromUrl) {
          const found = options.find((opt) => opt.id === payeeIdFromUrl);
          if (found) {
            setSelectedPayee(found);
          } else {
            // If not found in options, create a fallback option
            setSelectedPayee({ id: payeeIdFromUrl, label: payeeIdFromUrl });
          }
        }
        
        logInfo("Payee options loaded for payout history", { count: options.length });
      } catch (err) {
        logError("Failed to load payee options", {
          error: err.message,
        });
        // Non-blocking error - allow manual entry
      } finally {
        setLoadingOptions(false);
      }
    };
    
    loadPayeeOptions();
  }, [searchParams]);

  const fetchRuns = async (offset = 0) => {
    setLoading(true);
    setError(null);

    try {
      const filters = {
        payeeId: selectedPayee?.id || undefined,
        limit: 20,
        offset,
      };

      const result = await getPayoutRuns(filters);
      if (result.success) {
        setRuns(result.data.items || []);
        setPagination(result.data.pagination || { total: 0, limit: 20, offset: 0 });
      } else {
        setError(result.error || "Failed to load payout runs");
      }
    } catch (err) {
      logError("Failed to fetch payout runs", {
        error: err.message,
      });
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Only fetch if payee options have loaded (or if no payee selected)
    if (!loadingOptions) {
      fetchRuns(0);
    }
  }, [selectedPayee?.id, loadingOptions]);

  // Cleanup debounce timer on unmount
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  const handlePageChange = (event, page) => {
    const offset = (page - 1) * pagination.limit;
    fetchRuns(offset);
  };

  // Create payee lookup map for efficient name resolution
  const payeeById = useMemo(() => {
    return new Map((payeeOptions || []).map((opt) => [opt.id, opt]));
  }, [payeeOptions]);

  // Get payee display info (name + ID)
  const getPayeeDisplay = (payeeId) => {
    const opt = payeeById.get(payeeId);
    const name = opt?.meta?.name || null;
    return {
      name: name || null,
      id: payeeId || "N/A",
    };
  };

  // Get run type (CYCLE or IMMEDIATE)
  const getRunType = (run) => {
    // Prefer backend cycleType, fallback to period inference for backward compatibility
    if (run?.cycleType) return run.cycleType;
    return run?.period?.from && run?.period?.to ? "CYCLE" : "IMMEDIATE";
  };

  // Get human-readable run type label
  const getRunTypeLabel = (run) => {
    const t = getRunType(run);
    if (t === "CYCLE") return "Monthly (Payday)";
    if (t === "IMMEDIATE") return "Advance (Immediate)";
    return t || "Unknown";
  };

  if (loading && runs.length === 0) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <PageShell sx={{ maxWidth: 1400, mx: "auto" }}>
      <Box
        sx={{
          mb: { xs: 2, md: 3 },
          display: "flex",
          alignItems: "center",
          gap: 2,
          flexWrap: "wrap",
        }}
      >
        <Button startIcon={<ArrowBack />} onClick={() => navigate("/finance")}>
          Back
        </Button>
        <Typography variant="h4" sx={{ fontWeight: 700 }}>
          Payout History
        </Typography>
      </Box>

      <Card sx={{ mb: { xs: 2, md: 3 } }}>
        <CardContent sx={{ p: { xs: 2, md: 3 } }}>
          <Autocomplete
            fullWidth
            options={payeeOptions}
            value={selectedPayee}
            onChange={(_, value) => {
              // Clear any pending debounce timer since onChange handles the selection
              if (debounceTimerRef.current) {
                clearTimeout(debounceTimerRef.current);
                debounceTimerRef.current = null;
              }

              // Handle both object and string values (freeSolo mode)
              let payeeToSet = null;
              if (value) {
                if (typeof value === "string") {
                  // User typed a string value and pressed Enter or selected - create object
                  const trimmedValue = value.trim();
                  if (trimmedValue) {
                    payeeToSet = { id: trimmedValue, label: trimmedValue };
                    inputValueRef.current = trimmedValue;
                  }
                } else if (value.id) {
                  // User selected from options
                  payeeToSet = value;
                  inputValueRef.current = value.id;
                }
              } else {
                inputValueRef.current = "";
              }
              
              setSelectedPayee(payeeToSet);
              
              // Update URL params
              if (payeeToSet?.id) {
                navigate(`/finance/payouts/history?payeeId=${encodeURIComponent(payeeToSet.id)}`, { replace: true });
              } else {
                navigate("/finance/payouts/history", { replace: true });
              }
            }}
            onBlur={() => {
              // When dropdown closes, cancel any pending debounce timer
              // This prevents the search from triggering when user closes dropdown
              if (debounceTimerRef.current) {
                clearTimeout(debounceTimerRef.current);
                debounceTimerRef.current = null;
              }
            }}
            onClose={(_, reason) => {
              // When dropdown closes (for any reason), cancel pending debounce timer
              // This prevents blocking when user closes dropdown after typing
              if (debounceTimerRef.current) {
                clearTimeout(debounceTimerRef.current);
                debounceTimerRef.current = null;
              }
            }}
            getOptionLabel={(option) => {
              if (typeof option === "string") return option;
              return option?.label || option?.id || "";
            }}
            isOptionEqualToValue={(option, value) => {
              if (!option || !value) return false;
              const optionId = typeof option === "string" ? option : option.id;
              const valueId = typeof value === "string" ? value : value.id;
              return optionId === valueId;
            }}
            loading={loadingOptions}
            disabled={loadingOptions}
            freeSolo
            renderInput={(params) => (
              <TextField
                {...params}
                label="Filter by Payee"
                placeholder="Search by driver or partner name or ID..."
                size="small"
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {loadingOptions ? <CircularProgress size={20} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
            onInputChange={(_, newValue, reason) => {
              // Update the input value ref to track what user is typing
              inputValueRef.current = newValue || "";

              // Handle clear action immediately
              if (reason === "clear") {
                // Clear any pending debounce timer
                if (debounceTimerRef.current) {
                  clearTimeout(debounceTimerRef.current);
                  debounceTimerRef.current = null;
                }
                inputValueRef.current = "";
                setSelectedPayee(null);
                navigate("/finance/payouts/history", { replace: true });
                return;
              }

              // For input changes while typing, debounce the search
              // This handles the case where user types a freeSolo value but doesn't press Enter
              if (reason === "input") {
                // Clear any existing debounce timer
                if (debounceTimerRef.current) {
                  clearTimeout(debounceTimerRef.current);
                }

                const trimmedValue = newValue?.trim();
                if (trimmedValue && trimmedValue.length > 0) {
                  // Debounce the search - wait 800ms after user stops typing
                  // This prevents API calls on every keystroke
                  debounceTimerRef.current = setTimeout(() => {
                    // Only proceed if the input value hasn't changed (user stopped typing)
                    if (inputValueRef.current === newValue) {
                      // Check if it's a valid option first
                      const foundOption = payeeOptions.find((opt) => opt.id === trimmedValue);
                      const payeeToSet = foundOption || { id: trimmedValue, label: trimmedValue };
                      
                      setSelectedPayee(payeeToSet);
                      navigate(`/finance/payouts/history?payeeId=${encodeURIComponent(payeeToSet.id)}`, { replace: true });
                    }
                    debounceTimerRef.current = null;
                  }, 800);
                } else {
                  // Empty input - clear selection after a short delay
                  debounceTimerRef.current = setTimeout(() => {
                    // Only clear if input is still empty
                    if (!inputValueRef.current || inputValueRef.current.trim().length === 0) {
                      setSelectedPayee(null);
                      navigate("/finance/payouts/history", { replace: true });
                    }
                    debounceTimerRef.current = null;
                  }, 500);
                }
              }
            }}
            sx={{ minWidth: { xs: "100%", sm: 300 } }}
          />
        </CardContent>
      </Card>

      {error && (
        <Alert severity="error" sx={{ mb: { xs: 2, md: 3 } }}>
          {error}
        </Alert>
      )}

      {runs.length === 0 && !loading ? (
        <Alert severity="info">No payout runs found</Alert>
      ) : (
        <>
          <TableContainer component={Paper} sx={{ overflowX: "auto" }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Payout Run ID</TableCell>
                  <TableCell>Payee</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Executed</TableCell>
                  <TableCell>Period</TableCell>
                  <TableCell>Jobs</TableCell>
                  <TableCell>Net Amount</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {runs.map((run) => {
                  const payee = getPayeeDisplay(run.payeeId);
                  const runType = getRunType(run);

                  return (
                    <TableRow key={run.payoutRunId}>
                      <TableCell sx={{ fontFamily: "monospace", fontSize: "0.875rem" }}>
                        {run.payoutRunId}
                      </TableCell>
                      <TableCell>
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {payee.name || payee.id}
                          </Typography>
                          {payee.name && (
                            <Typography variant="caption" color="text.secondary" sx={{ fontFamily: "monospace" }}>
                              {payee.id}
                            </Typography>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          variant="outlined"
                          label={getRunTypeLabel(run)}
                          color={runType === "CYCLE" ? "primary" : "warning"}
                        />
                      </TableCell>
                      <TableCell>{formatDate(run.executedAt)}</TableCell>
                      <TableCell>
                        {run.period?.from && run.period?.to
                          ? `${formatDate(run.period.from)} - ${formatDate(run.period.to)}`
                          : "—"}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={`${run.jobCount} included`}
                          size="small"
                          color="success"
                          variant="outlined"
                          sx={{ mr: 1 }}
                        />
                        {run.excludedJobCount > 0 && (
                          <Chip
                            label={`${run.excludedJobCount} excluded`}
                            size="small"
                            color="error"
                            variant="outlined"
                          />
                        )}
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>
                        {formatCurrency(
                          run.totals?.netAfterRecovery ?? run.totals?.net
                        )}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={run.status}
                          size="small"
                          color={
                            run.status === "settled"
                              ? "success"
                              : run.status === "offered" || run.status === "executed"
                                ? "info"
                                : run.status === "cancelled"
                                  ? "default"
                                  : "warning"
                          }
                        />
                      </TableCell>
                      <TableCell>
                        <Button
                          size="small"
                          startIcon={<Visibility />}
                          onClick={() => navigate(`/finance/payouts/run/${run.payoutRunId}`)}
                        >
                          Settle
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>

          {pagination.total > pagination.limit && (
            <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
              <Pagination
                count={Math.ceil(pagination.total / pagination.limit)}
                page={Math.floor(pagination.offset / pagination.limit) + 1}
                onChange={handlePageChange}
                color="primary"
              />
            </Box>
          )}
        </>
      )}
    </PageShell>
  );
}
